import apiClient from './apiClient'
import {
  cilSpeedometer,
  cilEducation,
  cilGroup,
  cilLibrary,
  cilClock,
  cilTask,
  cilPeople,
  cilUser,
  cilUserFemale,
  cilMoney,
  cilBell,
  cilEnvelopeOpen,
  cilReportSlash,
  cilChartPie,
  cilSettings,
  cilLockLocked,
  cilRoom,
  cilHome,
  cilChart,
} from '@coreui/icons'

// Icon mapping for views
const iconMap = {
  'dashboard': cilSpeedometer,
  'courses': cilEducation,
  'groups': cilGroup,
  'lectures': cilLibrary,
  'attendance': cilClock,
  'homework': cilTask,
  'users': cilPeople,
  'students': cilUser,
  'professors': cilUserFemale,
  'payments': cilMoney,
  'notifications': cilBell,
  'messages': cilEnvelopeOpen,
  'reports': cilReportSlash,
  'analytics': cilChartPie,
  'settings': cilSettings,
  'admin': cilLockLocked,
  'rooms': cilRoom,
  'home': cilHome,
  'chart': cilChart,
}

// Get icon for a view code
const getIconForView = (viewCode) => {
  // Try exact match first
  if (iconMap[viewCode]) {
    return iconMap[viewCode]
  }
  
  // Try partial match (e.g., 'users.students' -> 'users')
  const baseCode = viewCode.split('.')[0]
  if (iconMap[baseCode]) {
    return iconMap[baseCode]
  }
  
  // Default icon
  return cilHome
}

// Helper function to recursively build navigation items (supports nested groups)
const buildNavItems = (children) => {
  return children.map(child => {
    const hasChildren = child.children && child.children.length > 0
    
    // If child is a group or has children, it's a nested dropdown
    if (child.type === 'group' || hasChildren) {
      return {
        name: child.name,
        items: hasChildren ? buildNavItems(child.children) : [{
          name: 'No accessible views',
          to: '#',
          disabled: true
        }]
      }
    }
    
    // Regular view item
    return {
      name: child.name,
      to: child.path || `/${child.code.replace(/\./g, '/')}`,
    }
  })
}

// Fetch navigation from backend
export const fetchNavigation = async () => {
  try {
    // Fetch both views and user permissions
    const [viewsResponse, permissionsResponse] = await Promise.all([
      apiClient.get('/views'),
      apiClient.get('/permissions/me')
    ])
    
    const allViews = viewsResponse.data.data.views || []
    const userPermissions = permissionsResponse.data.data.permissions || {}
    
    console.log('=== NAVIGATION DEBUG ===')
    console.log('Total views from API:', allViews.length)
    console.log('User has permissions for:', Object.keys(userPermissions).length, 'views')
    console.log('Sample permissions:', Object.keys(userPermissions).slice(0, 10))
    
    if (!allViews.length) {
      console.warn('No views found in database')
      return getDefaultNavigation()
    }
    
    // console.log('Fetched views:', allViews.length)
    // console.log('User permissions:', Object.keys(userPermissions))
    
    // Separate titles, groups, and views
    const allTitles = allViews.filter(view => view.type === 'title')
    const allGroups = allViews.filter(view => view.type === 'group')
    const regularViews = allViews.filter(view => view.type === 'view' || !view.type)
    
    // Filter titles that user has permission to see
    const accessibleTitles = allTitles.filter(title => {
      // Check if user has permission for this title
      const titlePerms = userPermissions[title.code]
      if (!titlePerms) return false
      
      // Check if user has at least one of: list, read, view actions
      return titlePerms.list || titlePerms.read || titlePerms.view
    })
    
    // Filter groups that user has permission to see
    const accessibleGroups = allGroups.filter(group => {
      // Check if user has permission for this group
      const groupPerms = userPermissions[group.code]
      if (!groupPerms) {
        return false
      }
      
      // Groups must have "view" action to be visible in navigation
      const hasViewPermission = groupPerms.view === true
      return hasViewPermission
    })
    
    // Filter views that user has permission to see
    const accessibleViews = regularViews.filter(view => {
      // Check the view's own permissions (not parent's)
      const viewPerms = userPermissions[view.code]
      
      if (!viewPerms) {
        return false
      }
      
      // Exclude detail views with parameters (like :id) from navigation
      // These should only be accessible by clicking from list views
      if (view.path && view.path.includes(':')) {
        return false
      }
      
      // Views must have "view" action to be visible in navigation
      const hasViewPermission = viewPerms.view === true
      
      return hasViewPermission
    })
    
    // console.log('Accessible views:', accessibleViews.length)
    // console.log('Accessible groups:', accessibleGroups.length)
    // console.log('Accessible titles:', accessibleTitles.length)
    
    // Build navigation tree
    const navigation = []
    
    // Combine accessible views and groups, sorted by display_order
    const allAccessibleItems = [...accessibleViews, ...accessibleGroups]
      .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
    
    // console.log('Accessible views count:', accessibleViews.length)
    // console.log('Accessible groups count:', accessibleGroups.length)
    // console.log('Payment views in accessible:', accessibleViews.filter(v => v.code && v.code.startsWith('payments')).map(v => v.name))
    
    // Combine titles and items, sorted by display_order
    const allItems = [
      ...accessibleTitles.map(t => ({ ...t, itemType: 'title' })),
      ...allAccessibleItems.map(item => ({ ...item, itemType: item.type }))
    ]
    
    // Log before sort
    // console.log('Before sort:', allItems.map(i => ({ 
    //   name: i.name, 
    //   order: i.display_order,
    //   orderType: typeof i.display_order
    // })))
    
    // Sort by display_order (handle null/undefined as 999)
    allItems.sort((a, b) => {
      const orderA = a.display_order !== null && a.display_order !== undefined ? Number(a.display_order) : 999
      const orderB = b.display_order !== null && b.display_order !== undefined ? Number(b.display_order) : 999
      return orderA - orderB
    })
    
    // console.log('After sort:', allItems.map(i => ({ 
    //   name: i.name, 
    //   order: i.display_order, 
    //   type: i.itemType,
    //   parent: i.parent_view_id 
    // })))
    
    // Verify sort is working
    // console.log('First 5 items by order:', allItems.slice(0, 5).map(i => `${i.display_order}: ${i.name}`))
    
    // Build navigation respecting the flat display_order
    // Items with parents will be grouped under their parent
    const itemsById = {}
    allAccessibleItems.forEach(item => {
      itemsById[item.id] = { ...item, children: [] }
    })
    
    // Build parent-child relationships
    allAccessibleItems.forEach(item => {
      if (item.parent_view_id && itemsById[item.parent_view_id]) {
        // console.log(`Attaching ${item.name} as child of parent ${itemsById[item.parent_view_id].name}`)
        itemsById[item.parent_view_id].children.push(itemsById[item.id])
      } else if (item.parent_view_id) {
        console.log(`⚠️  ${item.name} has parent ${item.parent_view_id} but parent not found in itemsById`)
      }
    })
    
    // Log items with children
    // console.log('Items with children:')
    Object.values(itemsById).forEach(item => {
      if (item.children.length > 0) {
        // console.log(`  ${item.name}: ${item.children.length} children -`, item.children.map(c => c.name))
      }
    })
    
    // Sort children by display_order
    Object.values(itemsById).forEach(item => {
      item.children.sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
    })
    
    // Build navigation in the exact order from display_order
    allItems.forEach((item) => {
      if (item.itemType === 'title') {
        // Add title directly to navigation
        // console.log(`Adding title: ${item.name}`)
        navigation.push({
          _component: 'CNavTitle',
          name: item.name,
        })
      } else if ((item.itemType === 'view' || item.itemType === 'group') && !item.parent_view_id) {
        // Only add root-level items (children will be added as subitems)
        if (!item.name) {
          console.warn(`No name found for item: ${item.code}`)
          return
        }
        
        // console.log(`Processing ${item.itemType}: ${item.name} (${item.code}), parent: ${item.parent_view_id || 'none'}`)
        
        const itemWithChildren = itemsById[item.id]
        const hasChildren = itemWithChildren && itemWithChildren.children && itemWithChildren.children.length > 0
        
        // console.log(`  Has children: ${hasChildren}, Children count: ${itemWithChildren?.children?.length || 0}`)
        if (hasChildren) {
          // console.log(`  Children:`, itemWithChildren.children.map(c => c.name))
        }
        
        const navItem = {
          name: item.name,
          // Use icon from database if available, otherwise use iconMap
          icon: item.icon ? getIconForView(item.icon) : getIconForView(item.code),
        }
        
        // If it's a group or has children, it's a dropdown (no 'to' property)
        // If it's a view with no children, it's a regular link (has 'to' property)
        if (item.itemType === 'group' || hasChildren) {
          // This is a group/dropdown - use recursive function to handle nested groups
          if (hasChildren) {
            navItem.items = buildNavItems(itemWithChildren.children)
            // console.log(`  Adding as dropdown with ${navItem.items.length} items`)
          } else {
            // Group has no accessible children - show message
            navItem.items = [{
              name: 'No accessible views',
              to: '#',
              disabled: true
            }]
            // console.log(`  Adding as dropdown with no accessible children message`)
          }
        } else {
          // This is a regular link - add 'to' property
          navItem.to = item.path || `/${item.code}`
          // console.log(`  Adding as link to: ${navItem.to}`)
        }
        
        navigation.push(navItem)
        // console.log(`  ✅ Added to navigation`)
      }
    })
    
    // console.log('Built navigation:', navigation)
    return navigation
  } catch (error) {
    console.error('Failed to fetch navigation:', error)
    return getDefaultNavigation()
  }
}

// Default navigation (fallback)
const getDefaultNavigation = () => {
  return [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: cilSpeedometer,
    },
    {
      name: 'Settings',
      to: '/settings',
      icon: cilSettings,
    },
  ]
}

export default {
  fetchNavigation,
  getDefaultNavigation,
}
